import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    DayHalf,
    ElapsedNumberLineSpec,
    isElapsedTimeQuestion,
    ProblemData,
    TimeDisplayOptions,
    TimeIntervalMinutes,
    TimeIntervalOptions,
    TimeQuestionOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import * as enTime from "../Files/Prompts/en/time.ts";
import * as esTime from "../Files/Prompts/es/time.ts";
import * as frTime from "../Files/Prompts/fr/time.ts";

const TIME_PROMPTS: Record<PrintedLanguage, typeof enTime> = { en: enTime, es: esTime, fr: frTime };

/**
 * Telling time problem generation.
 *
 * Like place value, these are not arithmetic - there is no operator and there
 * are no operands, just a time expressed two ways. Each problem carries the
 * hour and minute as numbers (which is what the clock face is drawn from) and
 * the question type plus the digital form as text.
 */

/** 3 and 5 -> "3:05". Hours are 1-12; minutes always two digits. */
export function formatTime(hours: number, minutes: number): string {
    return `${hours}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Where a hand points, in degrees clockwise from twelve.
 *
 * The hour hand moves continuously rather than jumping: at half past three it
 * sits between the 3 and the 4, which is exactly the thing that makes reading
 * a real clock harder than reading a diagram of one.
 */
export function handAngles(hours: number, minutes: number): { hour: number; minute: number } {
    return {
        hour: ((hours % 12) * 30 + minutes * 0.5) % 360,
        minute: (minutes * 6) % 360,
    };
}

/** A point on a circle of radius `length`, at `degrees` clockwise from twelve. */
export function pointOnFace(
    centre: number,
    length: number,
    degrees: number
): { x: number; y: number } {
    const radians = (degrees * Math.PI) / 180;
    return {
        x: centre + length * Math.sin(radians),
        y: centre - length * Math.cos(radians),
    };
}

/** Which question types a setting asks for. Mixed means both. */
function questionTypes(question: TimeQuestionOptions): TimeQuestionOptions[] {
    if (question === TimeQuestionOptions.Mixed) {
        return [TimeQuestionOptions.ReadTheClock, TimeQuestionOptions.DrawTheHands];
    }
    if (question === TimeQuestionOptions.MixedElapsed) {
        return [
            TimeQuestionOptions.HowMuchTime,
            TimeQuestionOptions.WhatTimeEnds,
            TimeQuestionOptions.WhatTimeStarted,
        ];
    }
    return [question];
}

/**
 * The tick-count bound for elapsed time on a number line (ADR 0044 §4): a
 * line whose ticks are coarser than the sheet's interval would put the marks
 * between ticks, which ADR 0033 §4 forbids - so the duration is bounded
 * instead of the tick spacing.
 */
const NUMBER_LINE_MAX_TICKS = 16;

/**
 * How often to label a tick on an elapsed-time number line.
 *
 * The endpoints (`from` and `to`) always have to be labelled - they are the
 * only two points the question gives - and `NumberLine.tsx` labels by tick
 * index (`i % labelEvery === 0`), so `labelEvery` has to divide `steps` or
 * the last tick would silently go unlabelled. Searching upward from
 * `steps / 8` for the first divisor keeps labels legible (roughly 50pt or
 * more apart at the widest, 16-tick line and a 430pt-wide drawing - measured
 * against a generated PDF, not assumed) while guaranteeing both ends always
 * carry a label, even when `steps` is prime and the only divisors are 1 and
 * itself.
 *
 * A crossing-noon line's labels carry their half ("11:45 a.m."), about twice
 * as wide, so it asks for at most four gaps (ADR 0090 §4): on the half-page
 * line, 300pt wide, eight would overprint.
 */
function labelEveryForElapsed(steps: number, maxGaps = 8): number {
    if (steps <= maxGaps) {
        return 1;
    }
    for (let candidate = Math.ceil(steps / maxGaps); candidate <= steps; candidate++) {
        if (steps % candidate === 0) {
            return candidate;
        }
    }
    return steps;
}

/**
 * Where a crossing-noon sheet's spans live (ADR 0090 §3): every start between
 * 6:00 a.m. and noon, every end between noon and 6:00 p.m., so a sentence
 * never has soccer practice at 3:00 in the morning.
 */
const NOON = 12 * 60;
const CROSSING_EARLIEST_START = 6 * 60;
const CROSSING_LATEST_END = 18 * 60;
const CROSSING_MAX_LABEL_GAPS = 4;

/** Minutes past midnight → which half of the day. */
export function halfOfDay(total: number): DayHalf {
    return total % (24 * 60) < NOON ? 'am' : 'pm';
}

/** Minutes past 12 → a clock time with hours 1-12. */
export function minutesToClock(total: number): { hours: number; minutes: number } {
    const hour24 = Math.floor(total / 60) % 12;
    return { hours: hour24 === 0 ? 12 : hour24, minutes: total % 60 };
}

/**
 * A duration in words: "2 hours", "45 minutes", "1 hour 35 minutes".
 * Per-language implementation lives in Files/Prompts (one time.ts per
 * locale), each written directly rather than routed through a shared
 * pluraliser - ADR 0051 §5 refuses a locale switch inside Grammar.ts by
 * name, and this is the same restraint applied to the one other place this
 * site pluralises a unit.
 */
export function durationInWords(totalMinutes: number, language: PrintedLanguage = 'en'): string {
    return TIME_PROMPTS[language].durationInWords(totalMinutes);
}

/**
 * The start and duration of a span that crosses noon (ADR 0090 §3), in
 * minutes past midnight. Still start-then-duration-then-derive-end on the
 * sheet's interval (ADR 0017 §3); only the bounds differ. The start is drawn
 * from the morning steps that leave room to reach past noon within the
 * longest allowed duration, and the duration from the steps that reach past
 * noon - never onto it, so neither endpoint is 12:00 - and stop by 6:00 p.m.
 */
function drawCrossingSpan(step: number, maxDuration: number): { startMin: number; durationMin: number } {
    const earliest = Math.max(CROSSING_EARLIEST_START, NOON + step - maxDuration);
    const startMin = earliest + getRandomInt(0, (NOON - step - earliest) / step) * step;
    const shortest = NOON - startMin + step;
    const longest = Math.min(maxDuration, CROSSING_LATEST_END - startMin);
    const durationMin = shortest + getRandomInt(0, (longest - shortest) / step) * step;
    return { startMin, durationMin };
}

/**
 * One elapsed-time problem. Drawn start-then-duration-then-derive-end, all on
 * the sheet's interval. Without `crossNoon` it stays inside one 12-hour sweep
 * so no problem crosses noon and none needs AM/PM (ADR 0017 §2, §3); with it,
 * every problem crosses noon and every time prints its half (ADR 0090 §3, §4).
 */
export function buildElapsedProblem(
    question: TimeQuestionOptions,
    interval: TimeIntervalOptions,
    mode: TimeDisplayOptions,
    key: number,
    language: PrintedLanguage = 'en',
    crossNoon = false
): ProblemData {
    const step = TimeIntervalMinutes[interval] ?? 15;
    const sweep = 12 * 60;

    let startMin: number;
    let durationMin: number;
    if (crossNoon) {
        // The number line's sixteen-tick bound (ADR 0044 §4) still holds.
        const maxDuration =
            mode === TimeDisplayOptions.NumberLine
                ? NUMBER_LINE_MAX_TICKS * step
                : CROSSING_LATEST_END - CROSSING_EARLIEST_START;
        ({ startMin, durationMin } = drawCrossingSpan(step, maxDuration));
    } else {
        // The off path draws exactly what it drew before ADR 0090, in the same
        // order, so every saved link prints the same sheet (ADR 0009).
        //
        // Duration: a positive multiple of the step, under 12 hours. A number
        // line additionally bounds it to at most sixteen ticks at the sheet's
        // interval (ADR 0044 §4) - the tick spacing itself is never coarsened,
        // which is what keeps every marker on a tick (ADR 0033 §4).
        const sweepCap = Math.max(1, Math.floor((sweep - step) / step));
        const maxDurSteps =
            mode === TimeDisplayOptions.NumberLine
                ? Math.min(sweepCap, NUMBER_LINE_MAX_TICKS)
                : sweepCap;
        durationMin = getRandomInt(1, maxDurSteps) * step;

        // Start: on the step grid, late enough that start + duration stays in the sweep.
        const maxStartSteps = Math.floor((sweep - 1 - durationMin) / step);
        startMin = getRandomInt(0, Math.max(0, maxStartSteps)) * step;
    }
    const endMin = startMin + durationMin;

    const halves = crossNoon ? TIME_PROMPTS[language].dayHalves : undefined;
    const start = crossNoon
        ? { ...minutesToClock(startMin), half: halfOfDay(startMin) }
        : minutesToClock(startMin);
    const end = crossNoon ? { ...minutesToClock(endMin), half: halfOfDay(endMin) } : minutesToClock(endMin);
    const durationText = durationInWords(durationMin, language);
    // "10:45 a.m." on a crossing sheet; "10:45" otherwise.
    const printed = (t: { hours: number; minutes: number; half?: DayHalf }) =>
        halves && t.half ? `${formatTime(t.hours, t.minutes)} ${halves[t.half]}` : formatTime(t.hours, t.minutes);

    const asked: "duration" | "end" | "start" =
        question === TimeQuestionOptions.HowMuchTime
            ? "duration"
            : question === TimeQuestionOptions.WhatTimeEnds
            ? "end"
            : "start";
    const answerText =
        asked === "duration"
            ? durationText
            : asked === "end"
            ? printed(end)
            : printed(start);

    const sentenceText =
        mode === TimeDisplayOptions.Sentence
            ? elapsedSentence(asked, printed(start), printed(end), durationText, language)
            : undefined;

    // The line's endpoints are the start and end already computed above -
    // both interval multiples by construction (ADR 0017 §3), asserted rather
    // than assumed in ElapsedTimeGenerator.test.ts.
    //
    // **Only the duration question gets a line** (ADR 0044 §1, corrected). A
    // time line's axis has to be labelled with clock times, and the span has
    // to reach the value being asked for, so on a "what time does it end"
    // line the end time is printed on the axis - the answer, on the
    // worksheet, in every single problem. Not marking it is not enough; it
    // was never the marker that gave it away. Labelling the axis in elapsed
    // minutes instead only moves the giveaway onto the duration question.
    //
    // The duration question is the one whose answer is not a value the axis
    // prints: both endpoints are given, and the gap between them is what is
    // asked. So the other two fall back to their digital presentation, which
    // keeps the question and changes only how it is shown.
    const line: ElapsedNumberLineSpec | undefined =
        mode === TimeDisplayOptions.NumberLine && asked === "duration"
            ? {
                  from: startMin,
                  to: endMin,
                  intervals: durationMin / step,
                  labelEvery: crossNoon
                      ? labelEveryForElapsed(durationMin / step, CROSSING_MAX_LABEL_GAPS)
                      : labelEveryForElapsed(durationMin / step),
                  marks: [startMin, endMin],
              }
            : undefined;

    return {
        key,
        // Keep the start time in `numbers`/`display[1]` so the shared
        // "readable clock time" checks still pass for these routes.
        numbers: [start.hours, start.minutes],
        operator: "",
        display: [question, formatTime(start.hours, start.minutes)],
        elapsed: {
            mode,
            start,
            end,
            ...(halves ? { halves } : {}),
            durationText,
            asked,
            answerText,
            sentenceText,
            line,
            chrome: {
                numberLinePrompt: TIME_PROMPTS[language].numberLinePrompts[asked],
                digitalPrompt: TIME_PROMPTS[language].timeChrome.digitalPrompt,
                afterWord: TIME_PROMPTS[language].timeChrome.afterWord,
                answerLabel: TIME_PROMPTS[language].timeChrome.answerLabel,
            },
        },
    };
}

function elapsedSentence(
    asked: "duration" | "end" | "start",
    s: string,
    e: string,
    durationText: string,
    language: PrintedLanguage
): string {
    const subjects = TIME_PROMPTS[language].sentenceSubjects;
    const [name, activity] = subjects[getRandomInt(0, subjects.length - 1)];
    const t = TIME_PROMPTS[language].timeSentences;
    if (asked === "duration") {
        return t.duration(name, activity, s, e);
    }
    if (asked === "end") {
        return t.end(name, activity, s, durationText);
    }
    return t.start(name, activity, e, durationText);
}

/**
 * One A.M.-or-P.M. question (ADR 0090 §1, §2): an event, a time on the
 * sheet's interval inside the event's window, and the half to circle. The
 * half is drawn first, then an event from it, so "always circle a.m." scores
 * about half. `display[1]` and `numbers` hold the printed time, as the other
 * time questions do.
 */
export function buildAmPmProblem(
    interval: TimeIntervalOptions,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    const step = TimeIntervalMinutes[interval] ?? 15;
    const prompts = TIME_PROMPTS[language];
    const half: DayHalf = getRandomInt(0, 1) === 0 ? 'am' : 'pm';
    const events = prompts.amPmEvents.filter((event) => event.half === half);
    const event = events[getRandomInt(0, events.length - 1)];
    const total = getRandomInt(Math.ceil(event.from / step), Math.floor(event.to / step)) * step;
    const time = minutesToClock(total);
    const printed = formatTime(time.hours, time.minutes);

    return {
        key,
        numbers: [time.hours, time.minutes],
        operator: "",
        display: [TimeQuestionOptions.AmOrPm, printed],
        amPm: {
            sentence: prompts.amPmSentence(event.text, printed),
            time,
            half,
            labels: prompts.dayHalves,
        },
    };
}

/** One clock question: a time, and which way round it is being asked. */
export function buildTimeProblem(
    question: TimeQuestionOptions,
    interval: TimeIntervalOptions,
    key: number
): ProblemData {
    const step = TimeIntervalMinutes[interval] ?? 15;
    const hours = getRandomInt(1, 12);
    // Steps of `step` minutes, so a quarter-hour sheet only ever shows
    // :00, :15, :30 and :45.
    const minutes = (getRandomInt(0, Math.floor(60 / step) - 1) * step) % 60;

    return {
        key,
        numbers: [hours, minutes],
        operator: "",
        display: [question, formatTime(hours, minutes)],
    };
}

/** A page-shaped set of clock questions. */
export function generateTimePages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.TimeQuestion ?? TimeQuestionOptions.ReadTheClock);
    const interval = settings.TimeInterval ?? TimeIntervalOptions.QuarterHour;
    const mode = settings.TimeDisplay ?? TimeDisplayOptions.TwoClocks;
    const elapsed = isElapsedTimeQuestion(settings.TimeQuestion ?? TimeQuestionOptions.ReadTheClock);
    const language = printedLanguageOf(settings);
    const crossNoon = elapsed && settings.CrossNoon === true;

    return generateProblemPages({
        settings,
        choices: types,
        build: (type, key) =>
            elapsed
                ? buildElapsedProblem(type, interval, mode, key, language, crossNoon)
                : type === TimeQuestionOptions.AmOrPm
                ? buildAmPmProblem(interval, key, language)
                : buildTimeProblem(type, interval, key),
        // An hour-only sheet has just twelve distinct times per question
        // type, so the shape has to identify the problem without the loop's
        // own `type` variable - both builders already record it as
        // `display[0]` (buildTimeProblem's question, or buildElapsedProblem's
        // `question` string, both stored the same way).
        fingerprint: (problem) =>
            elapsed
                ? `${problem.display![0]}@${problem.elapsed!.start.hours}:${problem.elapsed!.start.minutes}` +
                  `>${problem.elapsed!.end.hours}:${problem.elapsed!.end.minutes}`
                : problem.amPm
                ? `${problem.display![0]}@${problem.amPm.sentence}`
                : `${problem.display![0]}@${problem.display![1]}`,
    });
}
